import { API_BASE, getApiKey } from './config.js'

const PREFIX = `${API_BASE}/api/v1/onboarding`
const LONG_TIMEOUT_MS = 10 * 60 * 1000

function headers(json) {
  const h = {}
  const apiKey = getApiKey()
  if (apiKey) h['X-Ttai-Api-Key'] = apiKey
  if (json) h['Content-Type'] = 'application/json'
  return h
}

function queryString(params) {
  const parts = Object.entries(params || {})
    .filter(([, value]) => value !== undefined && value !== null && value !== false && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
  return parts.length ? `?${parts.join('&')}` : ''
}

async function fetchWithTimeout(url, init, timeoutMs = 0) {
  if (!timeoutMs || typeof AbortController === 'undefined') return fetch(url, init)
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(url, { ...init, signal: controller.signal })
  } catch (e) {
    if (e?.name === 'AbortError') throw new Error('Operazione interrotta: timeout client.')
    throw e
  } finally {
    window.clearTimeout(timer)
  }
}

async function jget(path, opts = {}) {
  const r = await fetchWithTimeout(`${PREFIX}${path}`, { headers: headers(false) }, opts.timeoutMs || 0)
  if (!r.ok) throw new Error(`GET ${path} -> ${r.status}`)
  return r.json()
}

async function jpost(path, body, opts = {}) {
  const r = await fetchWithTimeout(`${PREFIX}${path}`, {
    method: 'POST', headers: headers(true), body: JSON.stringify(body || {})
  }, opts.timeoutMs || 0)
  if (!r.ok) {
    const text = await r.text()
    let data = null
    try { data = text ? JSON.parse(text) : null } catch { data = null }
    const err = new Error(`POST ${path} -> ${r.status} ${text}`)
    err.status = r.status
    err.data = data
    err.body = text
    throw err
  }
  return r.json()
}

// La pagina PNG richiede l'header chiave: non si puo' usare <img src> diretto,
// quindi si scarica come blob e si crea un object URL.
async function pageObjectUrl(doc, page, dpi, opts = {}) {
  const r = await fetchWithTimeout(`${PREFIX}/page/${doc}/${page}.png?dpi=${dpi}`, { headers: headers(false) }, opts.timeoutMs || 0)
  if (!r.ok) throw new Error(`page ${doc}/${page} -> ${r.status}`)
  return URL.createObjectURL(await r.blob())
}

const classeParam = (classe) => `classe=${encodeURIComponent(classe)}`

export const api = {
  classes: () => jget('/classes'),
  ensureInferredForms: (classe) => jpost(`/class/ensure-inferred-forms?${classeParam(classe)}`, {}, { timeoutMs: LONG_TIMEOUT_MS }),
  resyncCatalog: (classe, body) => jpost(`/class/resync-catalog?${classeParam(classe)}`, body || {}, { timeoutMs: LONG_TIMEOUT_MS }),
  declareClass: (classe, body) => jpost(`/class/declare?${classeParam(classe)}`, body || {}, { timeoutMs: LONG_TIMEOUT_MS }),
  externalIdCheck: (classe) => jget(`/class/external-id-check?${classeParam(classe)}`),
  fields: (classe) => jget(`/class/fields?${classeParam(classe)}`),
  effective: (classe) => jget(`/profile/effective?${classeParam(classe)}`),
  fieldExtras: (classe, field) => jget(`/field-extras/${encodeURIComponent(field)}?${classeParam(classe)}`),
  arithRules: (classe) => jget(`/arith-rules?${classeParam(classe)}`),
  regions: (doc, profileId) => jget(`/regions/${doc}${profileId ? `?profile_id=${profileId}` : ''}`),
  wordlayer: (doc, opts = {}) => jget(`/wordlayer/${doc}${queryString({ force_ocr: opts.forceOcr, force_barcode: opts.forceBarcode })}`, { timeoutMs: LONG_TIMEOUT_MS }),
  wordlayerStatus: (doc, opts = {}) => jget(`/wordlayer/${doc}/status${queryString({ force_barcode: opts.forceBarcode })}`, { timeoutMs: LONG_TIMEOUT_MS }),
  barcode: (doc, force = false) => jget(`/barcode/${doc}${queryString({ force_barcode: force })}`, { timeoutMs: LONG_TIMEOUT_MS }),
  documentClass: (doc) => jget(`/document/${doc}/class`),
  applyProfile: (doc, classe, targetProfileId) =>
    jpost(`/document/${doc}/apply-profile?classe=${encodeURIComponent(classe)}&target_profile_id=${targetProfileId}`, {}, { timeoutMs: LONG_TIMEOUT_MS }),
  consolidateDraft: (doc, classe, targetProfileId) =>
    jpost(`/document/${doc}/consolidate-draft?classe=${encodeURIComponent(classe)}&target_profile_id=${targetProfileId}`, {}),
  pageObjectUrl,
  upsertProfile: (b) => jpost('/profile', b),
  addSample: (b) => jpost('/sample', b),
  upsertField: (b) => jpost('/field', b),
  addAnchor: (b) => jpost('/anchor', b),
  addRegion: (b) => jpost('/region', b),
  activate: (pid, by) => jpost(`/profile/${pid}/activate?activated_by=${encodeURIComponent(by)}`, {}),
  humanTruth: (b) => jpost('/human-truth', b),
  resetField: (b) => jpost('/reset-field', b),
  resetFieldAllSamples: (b) => jpost('/reset-field-all-samples', b),
  resetFieldAnchors: (b) => jpost('/reset-field-anchors', b),
  formsCatalog: () => jget('/forms-catalog'),
  confirmForm: (b) => jpost('/confirm-form', b),
  setFieldInheritTestata: (b) => jpost('/field-inherit-testata', b),
  clearFieldInheritTestata: (b) => jpost('/field-inherit-testata/clear', b),
  setDateConvention: (b) => jpost('/field-date-convention', b),
  setLabelDirection: (b) => jpost('/field-label-direction', b),
  saveArithRule: (classe, b) => jpost(`/arith-rules?${classeParam(classe)}`, b),
  disableArithRule: (classe, ruleId) => jpost(`/arith-rules/${encodeURIComponent(ruleId)}/disable?${classeParam(classe)}`, {}),
  setFieldExtract: (classe, field, body) => jpost(`/field-extract/${encodeURIComponent(field)}?${classeParam(classe)}`, body),
  previewFieldExtract: (classe, field, doc) => jget(`/field-extract/${encodeURIComponent(field)}/preview?${classeParam(classe)}&docnumber=${encodeURIComponent(doc)}`),
  previewListExtract: (body) => jpost('/list-extract', body, { timeoutMs: LONG_TIMEOUT_MS }),
  clearFieldExtract: (classe, field) => jpost(`/field-extract/${encodeURIComponent(field)}/clear?${classeParam(classe)}`, {}),
}

